import { useState, useEffect, useRef } from "react";
import { arrayOf, shape, object, string } from "prop-types";

import StyledLink from "../StyledLink/StyledLink.jsx";
import RichText from "../RichText/RichText.jsx";
import useMatchMedia from "../../common/useMatchMedia";

const propTypes = {
  header: string,
  description: string,
  people: arrayOf(
    shape({
      image: object,
      displayName: string,
      fullProgram: string,
      title: string,
      profileLink: string,
      additionalLink: string,
      additionalLinkText: string,
      summary: string,
    }),
  ),
};

const BioListing = (props) => {
  const { header, description, people } = props;

  const matchesDesktop = useMatchMedia("(min-width: 1024px)");

  const [showSidebar, setShowSidebar] = useState(false);
  const [activePerson, setActivePerson] = useState(null);
  const sidebarRef = useRef(null);

  // Listen for esc if the sidebar is open
  useEffect(() => {
    const keydownHandler = (e) => {
      if (e.key === "Escape") {
        setShowSidebar(false);
      }
    };

    if (showSidebar) {
      window.addEventListener("keydown", keydownHandler);
      return () => window.removeEventListener("keydown", keydownHandler);
    } else {
      window.removeEventListener("keydown", keydownHandler);
    }
  }, [showSidebar]);

  const bioListingSidebar = document.getElementById(
    "bio-listing-sidebar__content",
  );

  return (
    <section>
      {header && (
        <div className="component__header">
          <RichText>
            <div dangerouslySetInnerHTML={{ __html: header }}></div>
          </RichText>
        </div>
      )}
      {description && (
        <div className="component__description">
          <RichText>
            <p dangerouslySetInnerHTML={{ __html: description }}></p>
          </RichText>
        </div>
      )}

      <div className="bio-listing">
        {people?.map((person, i) => (
          // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
          <button
            className="bio-listing__wrapper"
            key={i}
            onClick={() => {
              setActivePerson(person);
              setShowSidebar(true);
              sidebarRef.current.focus();
              document.body.style.overflow = "hidden";
              bioListingSidebar.scrollTop = 0;
            }}
          >
            <div className="bio-listing__headshot">
              <img
                srcSet={`
             ${person.image.grid1x} 342w,
             ${person.image.grid2x} 684w,
           `}
                sizes="
              (min-width: 470px) 100%
            "
                src={person.image.grid1x}
                alt={person.image.alt ? person.image.alt : ""}
                loading="lazy"
              />
            </div>
            <div className="bio-listing__description-wrapper">
              <div className="bio-listing__description">
                <div className="bio-listing__name">{person.displayName}</div>
                <div className="bio-listing__title">
                  {person.fullProgram ? (
                    <>
                      {person.fullProgram} <br />
                    </>
                  ) : null}
                  {person.firstTitle ? person.firstTitle : null}
                </div>
              </div>
            </div>
          </button>
        ))}

        <div className={`bio-listing-sidebar ${showSidebar ? "active" : ""}`}>
          <div className="bio-listing-sidebar__btn-container">
            <button
              ref={sidebarRef}
              tabIndex={-1}
              className="bio-listing-sidebar__close-btn"
              onClick={() => {
                setShowSidebar(false);
                document.body.style.overflow = "auto";
              }}
            >
              <svg
                className="bio-listing-sidebar__close"
                role="img"
                aria-label="Close sidebar button"
                preserveAspectRatio="none"
                viewBox="6 6 14 14"
                width="20"
                height="20"
                fill="none"
                stroke="#FFFFFF"
                strokeWidth="1"
                xmlns="http://www.w3.org/2000/svg"
              >
                <line
                  x1="18"
                  y1="6"
                  x2="6"
                  y2="18"
                  stroke="#FFFFFF"
                  strokeWidth="1"
                />
                <line
                  x1="6"
                  y1="6"
                  x2="18"
                  y2="18"
                  stroke="#FFFFFF"
                  strokeWidth="1"
                />
              </svg>
            </button>
          </div>
          <div
            className="bio-listing-sidebar__content"
            id="bio-listing-sidebar__content"
          >
            <img
              srcSet={`
              ${activePerson?.image.mobileSidebar1x} 344w,
              ${activePerson?.image.desktopSidebar1x} 369w,
              ${activePerson?.image.mobileSidebar2x} 688w,
              ${activePerson?.image.desktopSidebar2x} 738w,
              ${activePerson?.image.tabletSidebar1x} 1107w,
            `}
              sizes="
              // (min-width: 1024px) 369px,
              // (min-width: 680px) 1107px,
              // (min-width: 470px) 344px,
              100%
            "
              src={activePerson?.image.mobileSidebar1x}
              alt={activePerson?.image.alt ? activePerson?.image.alt : ""}
              loading="lazy"
            />

            {activePerson?.profileLink ? (
              <StyledLink
                href={activePerson?.profileLink}
                className="bio-listing-sidebar__name"
              >
                {activePerson?.displayName}
              </StyledLink>
            ) : (
              <div className="bio-listing-sidebar__name">
                {activePerson?.displayName}
              </div>
            )}
            <div className="bio-listing-sidebar__title">
              {activePerson?.title}
            </div>
            {activePerson?.additionalLink ? (
              <a
                className="bio-listing-sidebar__link"
                href={activePerson?.additionalLink}
              >
                {activePerson?.additionalLinkText}
              </a>
            ) : null}

            <div
              className="bio-listing-sidebar__summary"
              dangerouslySetInnerHTML={{ __html: activePerson?.summary }}
            ></div>
          </div>
        </div>

        {matchesDesktop ? (
          // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
          <div
            className={`bio-listing-sidebar__overlay ${
              showSidebar ? "active" : ""
            }`}
            onClick={() => {
              setShowSidebar(false);
              document.body.style.overflow = "auto";
            }}
          ></div>
        ) : null}
      </div>
    </section>
  );
};

BioListing.propTypes = propTypes;
export default BioListing;
